import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

import { PostTimelineTypes } from './schema/Time'
import {
  UserSchema,
  CurrentUser,
  SignInUser,
  SignUpUser,
  UserUpdates,
  UserFollowsSchema,
  UserFollows,
  UsersSimple,
  UsersSimpleSchema,
} from './schema/User'
import {
  PostSchema,
  ResponsePostsSchema,
  ResponseRepliesSchema,
  Post,
  Reply,
  ResponsePosts,
  ResponseReplies,
} from './schema/Post'
import { errorHandler, dateFormatter } from './helpers'

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.VITE_BACKEND_URL,
  }),
  tagTypes: [
    'HomePosts',
    'Post',
    'UserPosts',
    'Replies',
    'User',
    'Follows',
    'UserCount',
  ],
  endpoints: (builder) => ({
    checkToken: builder.query<void, void>({
      query: () => ({
        url: `/auth/checkToken`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      }),
    }),
    registerUser: builder.mutation<{ success: boolean }, SignUpUser>({
      query: (q: SignUpUser) => ({
        url: `/auth/register`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: {
          username: q.username,
          email: q.email,
          password: q.password,
          dateOfBirth: q.dateOfBirth,
        },
        credentials: 'include',
      }),
      invalidatesTags(result, error, arg, meta) {
        if (result) {
          return [{ type: 'UserCount' }]
        } else {
          return []
        }
      },
    }),
    loginUser: builder.mutation<{ success: boolean }, SignInUser>({
      query: (q: SignInUser) => ({
        url: `/auth/login`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: { email: q.email, password: q.password },
        credentials: 'include',
      }),
    }),
    logoutUser: builder.mutation<{ success: boolean }, null>({
      query: () => ({
        url: `/auth/logout`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(apiSlice.util.resetApiState())
        } catch (err) {
          // cache flush failed
        }
      },
    }),
    verifyEmail: builder.mutation<
      { success: boolean },
      { email: string; token: string }
    >({
      query: (q) => ({
        url: `/auth/verifyEmail`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: { email: q.email, token: q.token },
        credentials: 'include',
      }),
      transformErrorResponse(bqv) {
        return errorHandler(bqv)
      },
    }),
    getUserWithToken: builder.query<CurrentUser, void>({
      query: (_) => ({
        url: `/user/getUserWithToken`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      }),
      providesTags: (result, error, args) => [
        { type: 'User' },
        { type: 'User', id: result && `${result.username}` },
      ],
      transformResponse: (response: { user: CurrentUser }, _, args) => {
        const createdAt = dateFormatter(
          response.user.dates.createdAt,
          'createdAt',
        ) as string
        const createdAtShort = dateFormatter(
          response.user.dates.createdAt,
          'createdAtShort',
        ) as string
        const dob = dateFormatter(
          response.user.dates.dateOfBirth,
          'dateOfBirth',
        ) as string
        const dobs = dateFormatter(
          response.user.dates.dateOfBirth,
          'dateOfBirthShort',
        ) as string
        const dobn = dateFormatter(
          response.user.dates.dateOfBirth,
          'dateOfBirthNum',
        ) as string
        const result = UserSchema.safeParse({
          ...response.user,
          dates: {
            ...response.user.dates,
            createdAt: createdAt ? createdAt : 'unknown',
            createdAtShort: createdAtShort ? createdAtShort : 'unknown',
            dateOfBirth: dob ? dob : 'unknown',
            dateOfBirthShort: dobs ? dobs : 'unknown',
            dateOfBirthNum: dobn,
          },
        })
        if (!result.success) {
          throw new Error('Invalid user data!')
        }

        return result.data
      },
    }),
    getUserWithUserName: builder.query<CurrentUser, { username: string }>({
      query: (args) => ({
        url: `/user/getUserWithUserName?username=${args.username}`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      }),
      providesTags: (result, error, args) => [
        { type: 'User', id: `${args.username}` },
      ],
      transformResponse: (response: { user: CurrentUser }, _, args) => {
        const createdAt = dateFormatter(
          response.user.dates.createdAt,
          'createdAt',
        ) as string
        const createdAtShort = dateFormatter(
          response.user.dates.createdAt,
          'createdAtShort',
        ) as string
        const dob = dateFormatter(
          response.user.dates.dateOfBirth,
          'dateOfBirth',
        ) as string
        const dobs = dateFormatter(
          response.user.dates.dateOfBirth,
          'dateOfBirthShort',
        ) as string
        const dobn = dateFormatter(
          response.user.dates.dateOfBirth,
          'dateOfBirthNum',
        ) as string
        const result = UserSchema.safeParse({
          ...response.user,
          dates: {
            ...response.user.dates,
            createdAt: createdAt ? createdAt : 'unknown',
            createdAtShort: createdAtShort ? createdAtShort : 'unknown',
            dateOfBirth: dob ? dob : 'unknown',
            dateOfBirthShort: dobs ? dobs : 'unknown',
            dateOfBirthNum: dobn,
          },
        })
        if (!result.success) {
          throw new Error('Invalid user data!')
        }

        return result.data
      },
    }),
    updateUserProfile: builder.mutation<
      { success: true },
      {
        currUsername: string
        updates: UserUpdates
      }
    >({
      query: (args) => ({
        url: `/user/updateUserProfile`,
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: {
          updates: { ...args.updates },
        },
      }),
      invalidatesTags: (result, error, args, meta) => {
        if (result) {
          return [
            { type: 'User', id: `${args.currUsername}` },
            { type: 'UserPosts' },
            { type: 'HomePosts' },
            { type: 'Post' },
            { type: 'Replies' },
            { type: 'Follows' },
          ]
        } else {
          return []
        }
      },
    }),
    setNewPost: builder.mutation<
      Post,
      {
        html: string | null
        media: string[]
        mediaPublicId: string[]
        mediaTypes: string[]
      }
    >({
      query: (args) => ({
        url: `/post/addPost`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: {
          html: args.html,
          media: args.media,
          mediaPublicId: args.mediaPublicId,
          mediaTypes: args.mediaTypes,
        },
      }),
      transformResponse: (response: Post, _, args) => {
        const timeline = dateFormatter(
          response.createdAt,
          'post',
        ) as PostTimelineTypes
        const transformed = {
          ...response,
          timeline: timeline,
        }
        const checked = PostSchema.safeParse(transformed)

        if (!checked.success) {
          throw new Error('Invalid post data!')
        }

        return checked.data
      },
      transformErrorResponse(response, meta, arg) {
        // error will not be handled here, it will just be thrown/returned. Cache will store error response and then custom component will catch the error and pass it to custom error display thru emitToCustomErrorDisplay
        return errorHandler(response)
      },
      async onQueryStarted(queryArgs, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          const timeline = dateFormatter(
            data.createdAt,
            'post',
          ) as PostTimelineTypes

          dispatch(
            apiSlice.util.updateQueryData(
              'getHomePostsWithToken',
              {
                page: 1,
              },
              (draft) => {
                draft.posts.unshift({ ...data, timeline: timeline })
              },
            ),
          )
          dispatch(
            apiSlice.util.upsertQueryData(
              'getPostById',
              {
                postId: data.postId,
                username: data.username,
              },
              {
                ...data,
                timeline: timeline,
              },
            ),
          )
          dispatch(
            apiSlice.util.updateQueryData(
              'getUserPostsWithToken',
              {
                page: 1,
                username: data.username,
              },
              (draft) => {
                draft.posts.unshift({ ...data, timeline: timeline })
              },
            ),
          )

          dispatch(
            apiSlice.util.updateQueryData(
              'getUserWithToken',
              undefined,
              (draft) => {
                const curr = draft.postCount ? draft.postCount : 0
                draft.postCount = curr + 1
              },
            ),
          )
        } catch (err) {
          // nothing to do here, error was already thrown to custom component and there's nothing to revert
        }
      },
    }),
    deletePost: builder.mutation<boolean, { postId: string; username: string }>(
      {
        query: (args) => ({
          url: `/post/deletePost?postId=${args.postId}`,
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        }),
        invalidatesTags: (result, error, args, meta) => {
          if (result) {
            return [
              { type: 'HomePosts' },
              { type: 'UserPosts' },
              { type: 'Post', id: `${args.username}/${args.postId}` },
              // we need to invalidate current user linked to token when we delete post because we are also deleting replies, we can't rely only on onQueryStarted to decrement reply counts in user limits... we actually can but it might be a bit complicated to code
              { type: 'User', id: `${args.username}` },
            ]
          } else {
            return []
          }
        },
      },
    ),
    getHomePostsWithToken: builder.query<ResponsePosts, { page: number }>({
      query: (args) => ({
        url: `/post/getHomePosts?page=${args.page}`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      }),
      providesTags: (res, err, args) => [
        { type: 'HomePosts', id: `posts/${args.page}` },
      ],
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        return `${endpointName}-${queryArgs.page}`
      },
      keepUnusedDataFor: 180,
      transformResponse: (response: ResponsePosts, _, args) => {
        const transformed = {
          posts: response.posts.map((post) => {
            const timeline = dateFormatter(
              post.createdAt,
              'post',
            ) as PostTimelineTypes
            return {
              ...post,
              timeline: timeline,
            }
          }),
          nextPage: response.nextPage,
        }

        const checked = ResponsePostsSchema.safeParse(transformed)

        if (!checked.success) {
          throw new Error('Invalid posts data!')
        }

        return checked.data
      },
      async onQueryStarted(queryArgs, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          data.posts.forEach((post) => {
            dispatch(
              apiSlice.util.upsertQueryData(
                'getPostById',
                {
                  postId: post.postId,
                  username: post.username,
                },
                post,
              ),
            )
          })
        } catch (err) {
          console.error('Failure to upsert query data', err)
        }
      },
    }),
    getPostById: builder.query<Post, { postId: string; username: string }>({
      query: (args) => ({
        url: `/post/getPost?postId=${args.postId}`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      }),
      providesTags: (result, error, args) => [
        { type: 'Post', id: `${args.username}/${args.postId}` },
      ],
      keepUnusedDataFor: 180,
      transformResponse: (response: Post, _, args) => {
        const timeline = dateFormatter(
          response.createdAt,
          'post',
        ) as PostTimelineTypes
        const transformed = {
          ...response,
          timeline: timeline,
        }
        const checked = PostSchema.safeParse(transformed)

        if (!checked.success) {
          throw new Error('Invalid post data!')
        }

        return checked.data
      },
    }),
    getUserPostsWithToken: builder.query<
      ResponsePosts,
      { page: number; username: string }
    >({
      query: (args) => ({
        url: `/post/getUserPosts?username=${args.username}&page=${args.page}`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      }),
      providesTags: (res, err, args) => [
        { type: 'UserPosts', id: `posts/${args.page}/${args.username}` },
      ],
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        return `${endpointName}-${queryArgs.username}-${queryArgs.page}`
      },
      keepUnusedDataFor: 180,
      transformResponse: (response: ResponsePosts, _, args) => {
        const transformed = {
          posts: response.posts.map((post) => {
            const timeline = dateFormatter(
              post.createdAt,
              'post',
            ) as PostTimelineTypes
            return {
              ...post,
              timeline: timeline,
            }
          }),
          nextPage: response.nextPage,
        }

        const checked = ResponsePostsSchema.safeParse(transformed)

        if (!checked.success) {
          throw new Error('Invalid posts data!')
        }

        return checked.data
      },
      async onQueryStarted(queryArgs, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          data.posts.forEach((post) => {
            dispatch(
              apiSlice.util.upsertQueryData(
                'getPostById',
                {
                  postId: post.postId,
                  username: post.username,
                },
                post,
              ),
            )
          })
        } catch (err) {
          console.error('Failure to upsert query data', err)
        }
      },
    }),
    updatePostLikes: builder.mutation<
      boolean,
      {
        postId: string
        username: string
        type: string
      }
    >({
      query: (args) => ({
        url: `/post/likePost`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: { type: args.type, id: args.postId },
        credentials: 'include',
      }),
      async onQueryStarted(queryArgs, { dispatch, queryFulfilled }) {
        const postPatchResult = dispatch(
          apiSlice.util.updateQueryData(
            'getPostById',
            {
              postId: queryArgs.postId,
              username: queryArgs.username,
            },
            (draft) => {
              if (queryArgs.type === 'add') {
                draft.likeCount += 1
              } else if (queryArgs.type === 'remove') {
                draft.likeCount = Math.max(0, draft.likeCount - 1)
              }
            },
          ),
        )
        const userPatchResult = dispatch(
          apiSlice.util.updateQueryData(
            'getUserWithToken',
            undefined,
            (draft) => {
              if (queryArgs.type === 'add') {
                const newArray =
                  draft.likedPosts === null ? [] : draft.likedPosts
                newArray.push(queryArgs.postId)
                draft.likedPosts = newArray
              } else if (queryArgs.type === 'remove') {
                const newArray =
                  draft.likedPosts === null
                    ? null
                    : draft.likedPosts?.filter((p) => p !== queryArgs.postId)
                draft.likedPosts = newArray
              }
            },
          ),
        )
        try {
          await queryFulfilled
        } catch {
          userPatchResult.undo()
          postPatchResult.undo()
        }
      },
      transformErrorResponse(bqv) {
        return errorHandler(bqv)
      },
    }),
    setNewReply: builder.mutation<
      Reply,
      {
        html: string
        postId: string
        posterName: string
      }
    >({
      query: (args) => ({
        url: `/post/addReply`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: {
          html: args.html,
          postId: args.postId,
        },
      }),
      invalidatesTags: (result, error, args, meta) => {
        if (result) {
          return [
            { type: 'Post', id: `${args.posterName}/${args.postId}` },
            { type: 'Replies', id: `replies/${args.postId}` },
          ]
        } else {
          return []
        }
      },
      transformErrorResponse(response, meta, arg) {
        // error will not be handled here, it will just be thrown/returned. Cache will store error response and then custom component will catch the error and pass it to custom error display thru emitToCustomErrorDisplay
        return errorHandler(response)
      },
      async onQueryStarted(queryArgs, { dispatch, queryFulfilled }) {
        const limitsPatchResult = dispatch(
          apiSlice.util.updateQueryData(
            'getUserWithToken',
            undefined,
            (draft) => {
              const curr = draft.replyCount ? draft.replyCount : 0
              draft.replyCount = curr + 1
            },
          ),
        )

        try {
          await queryFulfilled
        } catch (err) {
          limitsPatchResult.undo()
        }
      },
    }),
    getPostRepliesWithToken: builder.query<ResponseReplies, { postId: string }>(
      {
        query: (args) => ({
          url: `/post/getPostReplies?postId=${args.postId}&page=1`,
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        }),
        providesTags: (res, err, args) => [
          { type: 'Replies', id: `replies/${args.postId}` },
        ],
        serializeQueryArgs: ({ endpointName, queryArgs }) => {
          return `${endpointName}-${queryArgs.postId}`
        },
        keepUnusedDataFor: 180,
        transformResponse(response: ResponseReplies, meta, arg) {
          const transformed: ResponseReplies = {
            ...response,
            replies: response.replies.map((reply) => {
              return {
                ...reply,
                timeline: dateFormatter(
                  reply.createdAt,
                  'post',
                ) as PostTimelineTypes,
              }
            }),
            nextPage: response.nextPage,
          }

          const checked = ResponseRepliesSchema.safeParse(transformed)

          if (!checked.success) {
            throw new Error('Invalid posts data!')
          }

          return checked.data
        },
      },
    ),
    getMorePostReplies: builder.mutation<
      ResponseReplies,
      { page: number; postId: string }
    >({
      query: (args) => ({
        url: `/post/getMorePostReplies?postId=${args.postId}&page=${args.page}`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      }),
      async onQueryStarted(queryArgs, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(
            apiSlice.util.updateQueryData(
              'getPostRepliesWithToken',
              {
                postId: queryArgs.postId,
              },
              (draft) => {
                draft.nextPageCount = data.nextPageCount
                draft.nextPage = data.nextPage
                data.replies.forEach((reply) => {
                  draft.replies.push({
                    ...reply,
                    timeline: dateFormatter(
                      reply.createdAt,
                      'post',
                    ) as PostTimelineTypes,
                  })
                })
              },
            ),
          )
        } catch (err) {}
      },
    }),
    deleteReply: builder.mutation<
      {
        success: true
      },
      {
        replyId: string
        postId: string
        posterName: string
      }
    >({
      query: (args) => ({
        url: `/post/deleteReply?replyId=${args.replyId}&postId=${args.postId}`,
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      }),
      async onQueryStarted(queryArgs, { dispatch, queryFulfilled }) {
        const repliesPatchResult = dispatch(
          apiSlice.util.updateQueryData(
            'getPostRepliesWithToken',
            {
              postId: queryArgs.postId,
            },
            (draft) => {
              const filtered = draft.replies.filter((reply) => {
                return reply.replyId !== queryArgs.replyId
              })

              draft.replies = filtered
            },
          ),
        )
        const postPatchResult = dispatch(
          apiSlice.util.updateQueryData(
            'getPostById',
            {
              postId: queryArgs.postId,
              username: queryArgs.posterName,
            },
            (draft) => {
              draft.replyCount -= 1
            },
          ),
        )
        const limitsPatchResult = dispatch(
          apiSlice.util.updateQueryData(
            'getUserWithToken',
            undefined,
            (draft) => {
              const curr = draft.replyCount ? draft.replyCount : 0
              draft.replyCount = Math.max(0, curr - 1)
            },
          ),
        )
        try {
          await queryFulfilled
        } catch {
          repliesPatchResult.undo()
          postPatchResult.undo()
          limitsPatchResult.undo()
        }
      },
    }),
    // getReplyById is inactive / not used
    getReplyById: builder.query<
      Reply,
      {
        replyId: string
        replierUserName: string
      }
    >({
      query: (args) => ({
        url: `/post/getReply?replyId=${args.replyId}`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      }),
      keepUnusedDataFor: 120,
      // transformResponse is outdated, needs to go through zod type guarding check
      transformResponse: (response: Reply, _, args) => {
        const timeline = dateFormatter(response.createdAt, 'post') as {
          withinTheYear: boolean
          short: string
          long: string
          time: string
        }
        return {
          ...response,
          timeline: timeline,
        }
      },
    }),
    updateReplyLikes: builder.mutation<
      {
        success: true
      },
      {
        replyId: string
        replierUserName: string
        postId: string
        type: string
      }
    >({
      query: (args) => ({
        url: `/post/likeReply`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: { type: args.type, id: args.replyId },
        credentials: 'include',
      }),
      async onQueryStarted(queryArgs, { dispatch, queryFulfilled }) {
        const replyPatchResult = dispatch(
          apiSlice.util.updateQueryData(
            'getPostRepliesWithToken',
            {
              postId: queryArgs.postId,
            },
            (draft) => {
              const update = draft.replies.map((reply) => {
                if (reply.replyId === queryArgs.replyId) {
                  if (queryArgs.type === 'add') {
                    reply.likeCount += 1
                  } else if (queryArgs.type === 'remove') {
                    reply.likeCount = Math.max(0, reply.likeCount - 1)
                  }
                }
                return reply
              })

              draft.replies = update
            },
          ),
        )
        const userPatchResult = dispatch(
          apiSlice.util.updateQueryData(
            'getUserWithToken',
            undefined,
            (draft) => {
              if (queryArgs.type === 'add') {
                const newArray =
                  draft.likedPosts === null ? [] : draft.likedPosts
                newArray.push(queryArgs.replyId)
                draft.likedPosts = newArray
              } else if (queryArgs.type === 'remove') {
                const newArray =
                  draft.likedPosts === null
                    ? null
                    : draft.likedPosts?.filter((p) => p !== queryArgs.replyId)
                draft.likedPosts = newArray
              }
            },
          ),
        )
        try {
          await queryFulfilled
        } catch {
          replyPatchResult.undo()
          userPatchResult.undo()
        }
      },
      transformErrorResponse(bqv) {
        return errorHandler(bqv)
      },
    }),
    updateUserFollows: builder.mutation<
      {
        success: true
      },
      { type: string; currUser: string; otherUser: string }
    >({
      query: (args) => ({
        url: `/user/follow`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: { updates: { type: args.type, otherUser: args.otherUser } },
        credentials: 'include',
      }),
      invalidatesTags: (result, error, args) => {
        if (result) {
          return [
            { type: 'User', id: `${args.currUser}` },
            { type: 'User', id: `${args.otherUser}` },
            { type: 'Follows' },
            // { type: 'Follows', id: `follows/${args.currUser}` },
            // { type: 'Follows', id: `follows/${args.otherUser}` },
          ]
        } else {
          return []
        }
      },
    }),
    getUserFollows: builder.query<UserFollows, { username: string }>({
      query: (args) => ({
        url: `/user/getUserFollows?username=${args.username}`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      }),
      providesTags: (res, err, args) => [
        { type: 'Follows' },
        { type: 'Follows', id: `follows/${args.username}` },
      ],
      keepUnusedDataFor: 60, // default
      transformResponse(response: { userFollows: UserFollows }, meta, arg) {
        const checked = UserFollowsSchema.safeParse(response.userFollows)

        if (!checked.success) {
          throw new Error('Invalid user follows data!')
        }

        return checked.data
      },
    }),
    getUserSearched: builder.query<UsersSimple, null>({
      query: () => ({
        url: `/user/search`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      }),
      providesTags: (res, err, args) => [{ type: 'User' }],
      keepUnusedDataFor: 180,
      transformResponse(response: UsersSimple, meta, arg) {
        const checked = UsersSimpleSchema.safeParse(response)
        if (!checked.success) {
          throw new Error('Invalid users data!')
        }

        return checked.data
      },
    }),
    getUserCount: builder.query<{ userCount: number }, undefined>({
      query: () => ({
        url: `/user/count`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }),
      providesTags: (res, err, args) => [{ type: 'UserCount' }],
    }),
  }),
})

export const {
  useCheckTokenQuery,
  useGetUserWithTokenQuery,
  useRegisterUserMutation,
  useLoginUserMutation,
  useLogoutUserMutation,
  useVerifyEmailMutation,
  useGetHomePostsWithTokenQuery,
  useGetPostByIdQuery,
  useGetUserWithUserNameQuery,
  useGetUserPostsWithTokenQuery,
  useUpdateUserProfileMutation,
  useUpdatePostLikesMutation,
  useGetPostRepliesWithTokenQuery,
  useGetMorePostRepliesMutation,
  useGetReplyByIdQuery,
  useUpdateReplyLikesMutation,
  useDeletePostMutation,
  useDeleteReplyMutation,
  useUpdateUserFollowsMutation,
  useGetUserFollowsQuery,
  useGetUserSearchedQuery,
  useGetUserCountQuery,
} = apiSlice
